import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import PasswordLoginForm from './PasswordLoginForm';
import Brand from './Brand';
import StrokeAnimation from './StrokeAnimation';

const RESEND_COOLDOWN_S = 30;

const isDev = process.env.NODE_ENV === 'development';

const inputStyle = {
  width: '100%',
  height: 44,
  border: 'none',
  borderBottom: '1px solid var(--rule)',
  background: 'transparent',
  color: 'var(--ink)',
  fontSize: 17,
  fontFamily: 'var(--font-sans)',
  outline: 'none',
  padding: '4px 0',
  letterSpacing: '-0.005em',
};

const labelStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
  display: 'block',
  marginBottom: 6,
};

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('magic'); // 'magic' | 'sent' | 'password'

  // Code-fallback state (used in 'sent' mode).
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeSubmitting, setCodeSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Dev-only: backend returns the plaintext code when ENV != production AND
  // Resend send fails. Surfacing it here lets devs test without log-tailing.
  // Production users never see this because the field is never set.
  const [devCode, setDevCode] = useState('');

  // Tick down the resend cooldown once per second.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const sendMagicLink = async (targetEmail) => {
    const res = await fetch(`${API_URL}/auth/magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: targetEmail }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to send');
    }
    // Dev-only: backend returns devCode when send falls back to logging.
    setDevCode(data.devCode || '');
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      await sendMagicLink(email);
      setMode('sent');
      setCode('');
      setCodeError('');
      setResendCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setCodeSubmitting(true);
    setCodeError('');
    try {
      const res = await fetch(`${API_URL}/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Invalid or expired code');
      }
      login(data.token, data.teacher);
    } catch (err) {
      setCodeError(err.message);
    } finally {
      setCodeSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setCodeError('');
    try {
      await sendMagicLink(email);
      setCode('');
      setResendCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      setCodeError(err.message);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '5fr 6fr',
        background: 'var(--paper)',
      }}
      className="ink-login"
    >
      {/* ── Left: ink-wash editorial panel ── */}
      <div
        style={{
          position: 'relative',
          background: 'var(--paper-2)',
          borderRight: '1px solid var(--rule)',
          padding: '40px 48px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          minHeight: 600,
        }}
      >
        {/* Top: brand + meta */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Brand size="md" />
          <span
            className="t-mono"
            style={{
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--ink-3)',
            }}
          >
            Vol. 04 · Teachers
          </span>
        </div>

        {/* Middle: hanzi hero motif — stroke animation on 学 */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            padding: '32px 0',
          }}
        >
          <StrokeAnimation
            character="学"
            autoAnimate={true}
            size={340}
            strokeColor="#14110d"
            outlineColor="#d9d3c4"
            className="login-stroke"
          />
          {/* Vermillion seal */}
          <div
            style={{
              position: 'absolute',
              right: '14%',
              bottom: '18%',
              width: 56,
              height: 56,
              background: 'var(--vermillion)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-han)',
              fontSize: 28,
              borderRadius: 4,
              transform: 'rotate(-4deg)',
              boxShadow: '0 2px 0 rgba(20,17,13,0.08)',
              pointerEvents: 'none',
            }}
          >
            習
          </div>
        </div>

        {/* Bottom: quote */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <hr className="rule" style={{ marginBottom: 20 }} />
          <p
            style={{
              fontFamily: 'var(--font-han)',
              fontSize: 26,
              lineHeight: 1.35,
              color: 'var(--ink)',
              margin: 0,
              marginBottom: 8,
              letterSpacing: '0.04em',
            }}
          >
            學而時習之，不亦說乎
          </p>
          <p className="t-small" style={{ margin: 0, fontStyle: 'italic' }}>
            "To learn, and to practice in due season — is this not a pleasure?" — 論語
          </p>
        </div>
      </div>

      {/* ── Right: form ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 64px',
        }}
      >
        <div style={{ width: '100%', maxWidth: 380 }}>
          {mode === 'magic' && (
            <>
              <div className="t-eyebrow" style={{ marginBottom: 18 }}>Sign in or sign up · 登入 / 注册</div>
              <h1
                style={{
                  fontSize: 38,
                  lineHeight: 1.1,
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  margin: 0,
                  marginBottom: 12,
                  color: 'var(--ink)',
                }}
              >
                Start studying.
              </h1>
              <p className="t-body" style={{ marginBottom: 36 }}>
                Enter your email and we'll send a one-tap sign-in link. New here?
                Your account is created automatically.
              </p>

              <form onSubmit={handleMagicLink}>
                <div style={{ marginBottom: 32 }}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    style={inputStyle}
                    placeholder="you@school.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={(e) => (e.target.style.borderBottomColor = 'var(--ink)')}
                    onBlur={(e) => (e.target.style.borderBottomColor = 'var(--rule)')}
                    required
                  />
                </div>

                {error && (
                  <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 16 }}>{error}</p>
                )}

                <button
                  type="submit"
                  disabled={!email || loading}
                  style={{
                    width: '100%',
                    height: 52,
                    background: email && !loading ? 'var(--ink)' : 'var(--rule)',
                    color: email && !loading ? 'var(--paper)' : 'var(--ink-3)',
                    border: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    cursor: email && !loading ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 22px',
                    transition: 'background .15s',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  <span>{loading ? 'Sending…' : 'Send sign-in link'}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.18em' }}>↵</span>
                </button>
              </form>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '28px 0 20px' }}>
                <hr className="rule-soft" style={{ flex: 1 }} />
                <span
                  className="t-mono"
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: 'var(--ink-3)',
                  }}
                >
                  or
                </span>
                <hr className="rule-soft" style={{ flex: 1 }} />
              </div>

              <button
                type="button"
                onClick={() => setMode('password')}
                style={{
                  width: '100%',
                  height: 44,
                  background: 'transparent',
                  color: 'var(--ink)',
                  border: '1px solid var(--rule)',
                  fontSize: 14,
                  letterSpacing: '0.01em',
                  cursor: 'pointer',
                  borderRadius: 'var(--r2)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                Use password instead
              </button>

              {isDev && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/auth/dev-login`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                      });
                      if (!res.ok) throw new Error('Dev login failed');
                      const data = await res.json();
                      login(data.token, data.teacher);
                    } catch (err) {
                      setError(err.message);
                    }
                  }}
                  style={{
                    width: '100%',
                    height: 36,
                    marginTop: 24,
                    background: 'var(--gold)',
                    color: '#fff',
                    border: 'none',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    borderRadius: 'var(--r2)',
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Dev login (skip auth)
                </button>
              )}
            </>
          )}

          {mode === 'sent' && (
            <div>
              <div
                className="t-eyebrow"
                style={{ marginBottom: 18, color: 'var(--vermillion)' }}
              >
                Code sent · 已寄出
              </div>
              <h1
                style={{
                  fontSize: 38,
                  lineHeight: 1.1,
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  margin: 0,
                  marginBottom: 12,
                }}
              >
                Check your email.
              </h1>
              <p className="t-body" style={{ marginBottom: 28 }}>
                We sent a 6-digit code and a sign-in link to{' '}
                <strong style={{ color: 'var(--ink)' }}>{email}</strong>. Both expire
                in 15 minutes.
              </p>

              {isDev && devCode && (
                <div
                  style={{
                    marginBottom: 18,
                    padding: '10px 14px',
                    background: 'var(--gold)',
                    color: '#fff',
                    borderRadius: 'var(--r2)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    letterSpacing: '0.08em',
                  }}
                >
                  <span style={{ textTransform: 'uppercase', opacity: 0.85 }}>
                    Dev only ·{' '}
                  </span>
                  <span>code </span>
                  <strong style={{ letterSpacing: '0.18em', fontSize: 14 }}>
                    {devCode}
                  </strong>
                  <button
                    type="button"
                    onClick={() => setCode(devCode)}
                    style={{
                      marginLeft: 12,
                      background: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      border: 0,
                      padding: '3px 8px',
                      borderRadius: 3,
                      fontSize: 11,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      letterSpacing: '0.08em',
                    }}
                  >
                    fill
                  </button>
                </div>
              )}

              <form onSubmit={handleVerifyCode}>
                <label style={labelStyle}>Sign-in code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="000000"
                  style={{
                    width: '100%',
                    height: 60,
                    border: '1px solid var(--rule)',
                    borderRadius: 'var(--r2)',
                    background: 'var(--paper-2)',
                    color: 'var(--ink)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 28,
                    letterSpacing: '0.32em',
                    textAlign: 'center',
                    padding: '4px 0 4px 12px',
                    outline: 'none',
                    marginBottom: 18,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = 'var(--ink)')}
                  onBlur={(e) => (e.target.style.borderColor = 'var(--rule)')}
                />

                {codeError && (
                  <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 16 }}>
                    {codeError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={code.length !== 6 || codeSubmitting}
                  style={{
                    width: '100%',
                    height: 52,
                    background:
                      code.length === 6 && !codeSubmitting
                        ? 'var(--ink)'
                        : 'var(--rule)',
                    color:
                      code.length === 6 && !codeSubmitting
                        ? 'var(--paper)'
                        : 'var(--ink-3)',
                    border: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    cursor:
                      code.length === 6 && !codeSubmitting ? 'pointer' : 'not-allowed',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  {codeSubmitting ? 'Verifying…' : 'Verify code'}
                </button>
              </form>

              <div
                style={{
                  padding: '16px 18px',
                  borderLeft: '2px solid var(--vermillion)',
                  background: 'var(--vermillion-50)',
                  margin: '24px 0',
                }}
              >
                <p className="t-small" style={{ margin: 0, color: 'var(--ink-2)' }}>
                  Didn't see it? Check spam, or{' '}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0}
                    style={{
                      color: resendCooldown > 0 ? 'var(--ink-3)' : 'var(--ink)',
                      textDecoration: 'underline',
                      textUnderlineOffset: 3,
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                    }}
                  >
                    {resendCooldown > 0
                      ? `resend in ${resendCooldown}s`
                      : 'resend the email'}
                  </button>
                  .
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setMode('magic');
                  setDevCode('');
                  setCode('');
                  setCodeError('');
                }}
                style={{
                  width: '100%',
                  height: 44,
                  background: 'transparent',
                  color: 'var(--ink)',
                  border: '1px solid var(--rule)',
                  fontSize: 14,
                  cursor: 'pointer',
                  borderRadius: 'var(--r2)',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                Use a different email
              </button>
            </div>
          )}

          {mode === 'password' && (
            <>
              <div className="t-eyebrow" style={{ marginBottom: 18 }}>Sign in · 密碼</div>
              <h1
                style={{
                  fontSize: 38,
                  lineHeight: 1.1,
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  margin: 0,
                  marginBottom: 12,
                }}
              >
                Sign in with password.
              </h1>
              <p className="t-body" style={{ marginBottom: 36 }}>
                Or{' '}
                <button
                  type="button"
                  onClick={() => setMode('magic')}
                  style={{
                    color: 'var(--ink)',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                  }}
                >
                  send me a magic link
                </button>{' '}
                instead.
              </p>

              <PasswordLoginForm onSwitchToMagicLink={() => setMode('magic')} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
